import React, {
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { Card, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import PageTitle from '../layouts/PageTitle';
import { CommonCollections } from '../../App';
import {
  strapiUrl,
  NOMINATION_STATUS,
  REFEREE_STATUS,
  ELECTION_TYPE,
  CM_PRIMARY_ID,
  DECLINE_REASON,
} from '../../utils/constants';
import SectionalCommittees from './SectionalCommittees';
import DownloadFile from './Forms/Elements/DownloadFile';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { useRefereeCollections } from '../../context/RefereeReportCollections';
import { getReportRoundYear } from '../../utils/getReportRoundYear';
import { calculateColumnWidths } from '../../utils/calculateColumnWidths';
import { calculateDynamicStyle } from '../../utils/calculateDynamicStyle';
import { useAdminActionCollections } from '../../context/AdminActionCollections';
import { renderUpdatedSpan } from '../../utils/renderUpdatedSpan';
import ModalConfirmation from './ModalConfirmation';
import { axiosPost } from '../../utils/axiosPost';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { addCmseCommitteeToSectionalCommitteeArray } from '../../utils/addCmseCommitteeToSectionalCommitteeArray';
import { getCommitteeCandidateCount } from '../../utils/getCommitteeCandidateCount';
import CommitteeFilter from './NominationShortlist/CommitteeFilter';
import useLocalStorage from '../hooks/useLocalStorage';
import { devError } from '../../utils/devLog';

const TableIndependentAssessor = ({ nominationsInSectionalCommittee }) => {
  const {
    fellows,
    declineReasons,
    nonAasReferees,
    refereeStatuses,
    sectionalCommittees,
  } = useStaticCollections();
  const { candidates, committeeArray } = useAdminCollections();
  const { setNominationId, setRefereeReportId } = useContext(CommonCollections);

  const {
    refereeReports,
    nonAdminRefereeReportId,
    setNonAdminRefereeReportId,
    removeRefereeReport,
  } = useRefereeCollections();

  const { userDetails, originalUserDetails } = useLoggedInUserContextProvider();

  const { userCommittees } = useUserPermissions(userDetails, committeeArray);

  const cellRefs = useRef([]);
  const rowRefs = useRef({});
  const [columnWidths, setColumnWidths] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalBodyText, setModalBodyText] = useState('');
  const [deleteAssessor, setDeleteAssessor] = useState(false);
  const [refereeData, setRefereeData] = useState({});
  const { enableCommitteeAccess } = useAdminActionCollections();
  const isAdmin =
    userDetails?.roles?.includes('super-admin') ||
    userDetails?.roles?.includes('admin');

  const [scrolledIds] = useState(new Set());

  useEffect(() => {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

    const recentReports =
      refereeReports?.filter((report) => {
        const updatedAt = new Date(report.attributes.updatedAt);
        return updatedAt > fiveMinutesAgo && !scrolledIds.has(report.id);
      }) || [];

    if (recentReports.length > 0) {
      const mostRecentReport = recentReports.reduce((newest, current) => {
        const newestDate = new Date(newest.attributes.updatedAt);
        const currentDate = new Date(current.attributes.updatedAt);
        return currentDate > newestDate ? current : newest;
      });

      if (rowRefs.current[mostRecentReport.id]) {
        setTimeout(() => {
          const element = rowRefs.current[mostRecentReport.id];
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            element.style.transition = 'background-color 0.3s ease';
            element.style.backgroundColor = '#fff3cd';
            setTimeout(() => {
              element.style.backgroundColor = '';
            }, 2000);

            scrolledIds.add(mostRecentReport.id);
          }
        }, 500);
      }
    }
  }, [refereeReports, scrolledIds]);

  // Keep the old effect for backwards compatibility if needed
  useEffect(() => {
    if (nonAdminRefereeReportId && rowRefs.current[nonAdminRefereeReportId]) {
      setTimeout(() => {
        rowRefs.current[nonAdminRefereeReportId].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 500);
    }
  }, [nonAdminRefereeReportId, refereeReports]);

  const handleSelectClick = (referee, isRemove) => {
    setShowModal(true);
    setRefereeData(referee);
    setDeleteAssessor(isRemove);
    if (isRemove) {
      setModalBodyText(
        `Are you sure you want to remove **${referee.refereeName}** as an Independent Assessor?`,
      );
    } else {
      setModalBodyText(
        `Are you sure you want to reuse **${referee.refereeName}** as an Independent Assessor?`,
      );
    }
  };

  const handleCancelSelect = () => {
    setShowModal(false);
    setRefereeData({});
    setDeleteAssessor(false);
  };

  const handleConfirmSelect = async () => {
    if (deleteAssessor) {
      try {
        const response = await axiosPost('independent-assessor-remove', {
          nominationId: refereeData.attributes.nominationId,
          refereeReportId: refereeData.id,
        });

        if (response.status === 200) {
          if (nonAdminRefereeReportId === refereeData.id) {
            setNonAdminRefereeReportId(null);
          }

          removeRefereeReport(refereeData.id);

          setShowModal(false);
          setRefereeData({});
          setDeleteAssessor(false);
        }
      } catch (error) {
        devError('Error submitting independent assessor form:', error);
        setShowModal(false);
      }
    } else {
      try {
        const response = await axiosPost('independent-assessor-reuse', {
          data: refereeData,
        });

        if (response.status === 200) {
          const reportId = response.data?.data?.id || refereeData.id;

          setNonAdminRefereeReportId(reportId);

          setShowModal(false);
          setRefereeData({});
        }
      } catch (error) {
        devError('Error submitting independent assessor form:', error);
        setShowModal(false);
      }
    }
  };

  useEffect(() => {
    setColumnWidths(calculateColumnWidths(cellRefs.current, 150, 250));
  }, [nominationsInSectionalCommittee.length]);

  const canAddAssessors = (nomination) => {
    if (isAdmin) return true;

    if (userDetails.roles?.includes('committee-chair')) {
      let derivedCommitteeId = nomination.attributes.sectionalCommitteeId;

      if (
        nomination.attributes.electionTypeId ===
          ELECTION_TYPE.CORRESPONDING_MEMBER ||
        nomination.attributes.electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION
      ) {
        derivedCommitteeId = CM_PRIMARY_ID; // Corresponding Membership and special election
      }

      if (!derivedCommitteeId) return false;

      return userCommittees.some(
        (committee) => Number(committee.id) === Number(derivedCommitteeId),
      );
    }

    return false;
  };

  return (
    <>
      {nominationsInSectionalCommittee.map((nomination) => {
        const filteredReports = refereeReports?.filter(
          (report) =>
            report.attributes.nominationId === nomination.id &&
            !report.attributes.refereeArchived,
        );
        const linkAssessorText = 'Add assessors';
        const linkAssessorIcon = 'plus';

        return (
          <Card
            key={nomination.id}
            className="card-two mb-xl-5 table-responsive"
          >
            <Card.Body>
              <Card.Title className="mb-xl-4">
                <div className="row">
                  <div className="col d-flex justify-content-between align-items-center">
                    <div>
                      {candidates[nomination.attributes.candidateId]}{' '}
                      <Link
                        to="/form-view"
                        onClick={() => {
                          setNominationId(nomination.id);
                        }}
                      >
                        <span title="View">
                          <i className="bi bi-eye view-button" />
                        </span>
                      </Link>
                      {nomination.attributes.overlapCommittee &&
                        nomination.attributes.overlapSectionalCommitteeId !==
                          null && (
                          <span className="badge badge-primary m-1">
                            <i>
                              {
                                sectionalCommittees[
                                  nomination.attributes.sectionalCommitteeId
                                ]
                              }{' '}
                              Overlaps with{' '}
                              {
                                sectionalCommittees[
                                  nomination.attributes
                                    .overlapSectionalCommitteeId
                                ]
                              }
                            </i>
                          </span>
                        )}
                      {nomination.attributes.nominationStatusTypeId &&
                        nomination.attributes.nominationStatusTypeId ===
                          NOMINATION_STATUS.RECOMMENDED && (
                          <span className="badge badge-council-shortlisted ms-1">
                            <i className="fas fa-star me-1" />
                            Recommended to Council
                          </span>
                        )}
                    </div>
                    {((nomination.attributes.electionTypeId ===
                      ELECTION_TYPE.ORDINARY &&
                      filteredReports.length < 9) ||
                      (nomination.attributes.electionTypeId ===
                        ELECTION_TYPE.CORRESPONDING_MEMBER &&
                        filteredReports.length < 7) ||
                      (nomination.attributes.electionTypeId ===
                        ELECTION_TYPE.SPECIAL_ELECTION &&
                        filteredReports.length < 7)) &&
                      (userDetails && !enableCommitteeAccess && !isAdmin
                        ? null
                        : canAddAssessors(nomination) && (
                            <Link
                              to="/independent-assessor"
                              onClick={() => {
                                setNominationId(nomination.id);
                                setRefereeReportId(null);
                              }}
                              onKeyPress={(event) => {
                                if (event.key === 'Enter') {
                                  setNominationId(nomination.id);
                                  setRefereeReportId(null);
                                }
                              }}
                            >
                              <button type="button" className="btn btn-success">
                                <span className="btn-icon-start text-info">
                                  <i
                                    className={`fa fa-${linkAssessorIcon} color-info`}
                                  />
                                </span>
                                {linkAssessorText}
                              </button>
                            </Link>
                          ))}
                  </div>
                </div>
              </Card.Title>
              <Table bordered hover striped className="dataTable">
                <thead className="thead-dark">
                  <tr>
                    <th>
                      <b>Name</b>
                    </th>
                    <th>
                      <b>Status</b>
                    </th>
                    <th>
                      <b>Type</b>
                    </th>
                    <th>
                      <b>Round</b>
                    </th>
                    <th className="text-center" aria-label="Actions">
                      <b>Actions</b>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((referee, index) => {
                    const reportYear = getReportRoundYear(referee.attributes);
                    const candidateName =
                      candidates[nomination.attributes.candidateId];
                    const candidateWithoutSpaces = candidateName
                      ? candidateName.replace(/\s/g, '')
                      : '';

                    referee.refereeName = referee.attributes.refereeFellowId
                      ? fellows[referee.attributes.refereeFellowId]
                      : nonAasReferees[referee.attributes.nonAasRefereeId];

                    referee.candidate =
                      candidates[nomination.attributes.candidateId];
                    referee.electionTypeId =
                      nomination.attributes.electionTypeId;
                    referee.sectionalCommittee =
                      sectionalCommittees[
                        nomination.attributes.sectionalCommitteeId
                      ];
                    referee.testEmail = originalUserDetails.email;
                    let reason = '';
                    const {
                      refereeStatusId,
                      declineReasonId,
                      declineReasonOther,
                      extensionReason,
                      extensionReasonApprovalNote,
                      createdAt,
                    } = referee.attributes;

                    if (refereeStatusId === REFEREE_STATUS.DECLINED) {
                      reason =
                        declineReasonId === DECLINE_REASON.OTHER
                          ? declineReasonOther
                          : declineReasons[declineReasonId];
                    } else if (
                      refereeStatusId === REFEREE_STATUS.EXTENSION_REQUESTED
                    ) {
                      reason = extensionReason;
                    } else if (
                      refereeStatusId === REFEREE_STATUS.EXTENSION_APPROVED
                    ) {
                      reason = extensionReasonApprovalNote;
                    }
                    return (
                      <tr
                        key={index}
                        ref={(el) => (rowRefs.current[referee.id] = el)}
                      >
                        <td
                          ref={(el) => (cellRefs.current[index] = el)}
                          style={calculateDynamicStyle(columnWidths[0])}
                        >
                          {referee.refereeName}{' '}
                          {renderUpdatedSpan(createdAt, 5, 'New')}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[index + filteredReports.length] =
                              el)
                          }
                          style={calculateDynamicStyle(columnWidths[1])}
                        >
                          {reason ? (
                            <>
                              <span>
                                <b>
                                  {
                                    refereeStatuses[
                                      referee.attributes.refereeStatusId
                                    ]
                                  }
                                  {' : '}
                                </b>
                              </span>
                              {refereeStatusId ===
                              REFEREE_STATUS.EXTENSION_APPROVED ? (
                                <>
                                  <br />
                                  <span>Reason: {extensionReason}</span>
                                  <br />
                                  <span>
                                    Approval Note: {extensionReasonApprovalNote}
                                  </span>
                                </>
                              ) : (
                                <span>{reason}</span>
                              )}
                            </>
                          ) : (
                            <span>
                              {
                                refereeStatuses[
                                  referee.attributes.refereeStatusId
                                ]
                              }
                            </span>
                          )}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[
                              index + filteredReports.length * 2
                            ] = el)
                          }
                          style={calculateDynamicStyle(columnWidths[2])}
                        >
                          {referee.attributes.independentAssessor === true
                            ? 'Independent Assessor'
                            : 'Referee'}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[
                              index + filteredReports.length * 3
                            ] = el)
                          }
                          style={calculateDynamicStyle(columnWidths[3])}
                        >
                          {reportYear}
                        </td>
                        <td
                          ref={(el) =>
                            (cellRefs.current[
                              index + filteredReports.length * 4
                            ] = el)
                          }
                          style={calculateDynamicStyle(columnWidths[4])}
                          className="text-center"
                        >
                          {referee.attributes.report.data !== null && (
                            <DownloadFile
                              text={`Report Uploaded Year : ${reportYear}`}
                              url={`${strapiUrl}${referee.attributes.report.data.attributes.url}`}
                              name={`${candidateWithoutSpaces}${referee.refereeName}`}
                              buttonClassName="action-button-two"
                              iconClassName="download-button"
                              showFileName={false}
                              showText={false}
                            />
                          )}

                          {referee.attributes.independentAssessor === true &&
                            canAddAssessors(nomination) && (
                              <>
                                {referee.attributes.requestEmailDate && (
                                  <Link
                                    to="/independent-assessor"
                                    onClick={() => {
                                      setNominationId(
                                        referee.attributes.nominationId,
                                      );
                                      setRefereeReportId(referee.id);
                                    }}
                                    onKeyPress={(event) => {
                                      if (event.key === 'Enter') {
                                        setNominationId(
                                          referee.attributes.nominationId,
                                        );
                                        setRefereeReportId(referee.id);
                                      }
                                    }}
                                  >
                                    <i className="bi bi-pen action-button" />
                                  </Link>
                                )}
                                {!referee.attributes.requestEmailDate && (
                                  <Link
                                    onClick={() =>
                                      handleSelectClick(referee, false)
                                    }
                                  >
                                    <span title="Reuse Independent Assessor">
                                      <i className="bi bi-arrow-repeat view-button" />
                                    </span>
                                  </Link>
                                )}
                                <Link
                                  onClick={() =>
                                    handleSelectClick(referee, true)
                                  }
                                >
                                  <span title="Remove Independent Assessor">
                                    <i className="bi bi-trash action-button bg-danger" />
                                  </span>
                                </Link>
                              </>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        );
      })}
      <ModalConfirmation
        showModal={showModal}
        handleCancelSelect={handleCancelSelect}
        bodyText={modalBodyText}
        handleConfirmSelect={handleConfirmSelect}
      />
    </>
  );
};

const AdminIndependentAssessor = () => {
  const { enableCommitteeAccess } = useAdminActionCollections();
  const { sectionalCommitteesArray } = useStaticCollections();
  const { userDetails } = useLoggedInUserContextProvider();
  const {
    committeeArray,
    nominationArray,
    candidateArray,
    nominationStateArray,
    proposerSeconder,
  } = useAdminCollections();

  const { isAdmin, isSCSecretary, isSCChair } = useUserPermissions(
    userDetails,
    committeeArray,
  );

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [selectedCommittee, setSelectedCommittee] = useLocalStorage(
    'independentAssessorSelectedCommittee',
    null,
  );

  const sectionalCommitteesWithCmse = addCmseCommitteeToSectionalCommitteeArray(
    sectionalCommitteesArray,
    true,
  );

  useEffect(() => {
    if (!enableCommitteeAccess) {
      setNotificationMessage(
        ' Independent Assessor submissions are now closed, if you are required to submit a report for any candidate, please contact fellowship@science.org.au.',
      );
      setNotificationTitle('Independent Assessor submissions are now closed');
    }
  }, [enableCommitteeAccess]);

  const handleCommitteeChange = (committeeId) => {
    setSelectedCommittee(committeeId);
  };

  const handleGetCommitteeCandidateCount = useCallback(
    (committeeId) => {
      if (!nominationArray || !candidateArray || !proposerSeconder) {
        return 0;
      }

      return getCommitteeCandidateCount({
        committeeId,
        nominationArray,
        candidateArray,
        nominationStateArray,
        proposerSeconder,
        showMyAllocations: false,
        isCouncilMember: false,
        isAdmin,
        userId: userDetails?.id,
        selectedNominationStatus: [NOMINATION_STATUS.SHORTLISTED],
        selectedRankingFilter: null,
      });
    },
    [
      nominationArray,
      candidateArray,
      nominationStateArray,
      proposerSeconder,
      isAdmin,
      userDetails?.id,
    ],
  );

  return (
    <>
      <PageTitle
        motherMenu="Nominations"
        activeMenu="Independent Assessors"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />

      {(isAdmin || isSCSecretary || isSCChair) && (
        <div className="table-controls mt-xl-5 mb-4 d-flex align-items-center gap-2">
          <CommitteeFilter
            selectedCommittee={selectedCommittee}
            onCommitteeChange={handleCommitteeChange}
            committees={sectionalCommitteesWithCmse}
            getCount={handleGetCommitteeCandidateCount}
          />
        </div>
      )}

      <SectionalCommittees
        TableComponent={TableIndependentAssessor}
        statusTypes={[
          NOMINATION_STATUS.SHORTLISTED,
          NOMINATION_STATUS.RECOMMENDED,
        ]}
        selectedCommittee={selectedCommittee}
      />
    </>
  );
};

TableIndependentAssessor.propTypes = {
  nominationsInSectionalCommittee: PropTypes.array,
};

export default AdminIndependentAssessor;
